import { Component, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { CsvExportService } from '../../../../services/csv-export/csv-export.service'
import { TrygroupDashboardSettingsService } from '../../../../services/trygroup-dashboard-settings/trygroup-dashboard-settings.service'

type CsvRow = Record<string, string>

@UntilDestroy()
@Component({
  selector: 'app-trygroup-ng-analysis-export-settings-page',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <section class="settings-panel">
      <div class="panel-header">
        <div>
          <h3>NGパターン分析エクスポート</h3>
          <p>教室長ごとの因子と直近5回のNGパターンヒット数をCSV出力します。</p>
        </div>
      </div>

      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="isExporting"
        (click)="downloadNgAnalysis()"
      >
        <span class="button-content">
          @if (isExporting) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>download</mat-icon>
          }
          {{ isExporting ? 'エクスポート中...' : 'CSVダウンロード' }}
        </span>
      </button>

      @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .settings-panel {
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
      }

      .panel-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 18px;
      }

      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
      }

      p {
        margin: 6px 0 0;
        color: #64748b;
        font-size: 13px;
      }

      .button-content {
        display: inline-flex;
        gap: 8px;
        align-items: center;
      }

      .button-content mat-icon {
        width: 18px;
        height: 18px;
        font-size: 18px;
      }

      .error {
        margin-top: 16px;
        color: #dc2626;
        font-weight: 700;
      }
    `,
  ],
})
export class TrygroupNgAnalysisExportSettingsPageComponent {
  isExporting = false
  errorMessage = ''

  constructor(
    private trygroupDashboardSettingsService: TrygroupDashboardSettingsService,
    private csvExportService: CsvExportService,
  ) {}

  /** NGパターン分析データを取得し、JST日付付きのCSVとしてダウンロードする。 */
  downloadNgAnalysis(): void {
    if (this.isExporting) {
      return
    }

    this.isExporting = true
    this.errorMessage = ''
    this.trygroupDashboardSettingsService
      .exportNgAnalysis()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (rows) => {
          this.isExporting = false
          const [headers, ...data] = rows
          if (!headers || data.length === 0) {
            this.errorMessage = 'エクスポート対象データがありません。'
            return
          }

          this.csvExportService.downloadCsv(
            `ng_analysis_export_${formatJstYyyymmdd()}.csv`,
            buildCsvHeaders(headers),
            buildCsvRows(headers, data),
          )
        },
        error: () => {
          this.isExporting = false
          this.errorMessage = 'NGパターン分析CSVのエクスポートに失敗しました。'
        },
      })
  }
}

/** 2D配列の先頭行を CsvExportService のヘッダー定義へ変換する。 */
function buildCsvHeaders(
  headers: string[],
): { label: string; key: keyof CsvRow }[] {
  return headers.map((label, index) => ({
    label,
    key: `column${index}`,
  }))
}

/** 2D配列のデータ行を CsvExportService の行オブジェクトへ変換する。 */
function buildCsvRows(headers: string[], rows: string[][]): CsvRow[] {
  return rows.map((row) => {
    return Object.fromEntries(
      headers.map((_, index) => [`column${index}`, row[index] ?? '']),
    ) as CsvRow
  })
}

/** JST基準のYYYYMMDD文字列を返す。 */
function formatJstYyyymmdd(date = new Date()): string {
  const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
  return jstDate.toISOString().slice(0, 10).replace(/-/g, '')
}
